/**
 * Client HTTP de l'administration.
 *
 * Trois règles tiennent tout le fichier :
 *
 * 1. `credentials: "include"` sur chaque requête. La session vit dans un cookie
 *    httpOnly posé par l'API : JavaScript ne peut pas le lire, donc ne peut pas
 *    le poser lui-même dans un en-tête. Sans cette option, `fetch` n'enverrait
 *    rien et toutes les routes protégées répondraient 401.
 * 2. Un 401 signifie « la session n'existe plus ». On ramène l'utilisateur à
 *    l'écran de connexion plutôt que d'afficher une erreur incompréhensible.
 * 3. Les erreurs de validation de l'API arrivent champ par champ
 *    (`{ erreurs: { email: "…" } }`) et doivent ressortir telles quelles pour
 *    s'afficher sous le bon champ de formulaire.
 */

/** Erreurs de validation indexées par nom de champ. `_global` vise le formulaire entier. */
export type ErreursChamps = Record<string, string>;

/** Toute réponse non-2xx est convertie en cette erreur, jamais en `throw` nu. */
export class ErreurApi extends Error {
  readonly statut: number;
  readonly erreurs: ErreursChamps;

  constructor(statut: number, message: string, erreurs: ErreursChamps = {}) {
    super(message);
    this.name = "ErreurApi";
    this.statut = statut;
    this.erreurs = erreurs;
  }

  /** Message à afficher pour un champ donné, ou `undefined` s'il est valide. */
  pourChamp(champ: string): string | undefined {
    return this.erreurs[champ];
  }

  /** Message général : l'erreur `_global` si elle existe, sinon le message brut. */
  get messageGlobal(): string {
    return this.erreurs._global ?? this.message;
  }
}

type Redirection = () => void;

let redirigerVersConnexion: Redirection | null = null;

/**
 * Branche la redirection sur 401. Le routeur n'existe pas encore au moment où
 * ce module est chargé : c'est `App` qui l'installe une fois monté, ce qui
 * évite un `window.location` brutal qui rechargerait toute l'application.
 */
export function surSessionExpiree(redirection: Redirection | null): void {
  redirigerVersConnexion = redirection;
}

export type OptionsRequete = {
  methode?: "GET" | "POST" | "PATCH" | "DELETE";
  corps?: unknown;
  /**
   * Neutralise la redirection automatique sur 401. Indispensable pour la
   * vérification de session au démarrage : un 401 y est une réponse normale
   * (« personne n'est connecté »), pas une session qui vient d'expirer. Sans
   * cette échappatoire, l'écran de connexion se redirigerait vers lui-même.
   */
  toleranteAuxAnonymes?: boolean;
  signal?: AbortSignal;
};

/** Extrait `{erreurs}` ou `{erreur}` d'une réponse en échec, sans jamais lever. */
async function lireErreur(reponse: Response): Promise<{ message: string; erreurs: ErreursChamps }> {
  const repli = `Erreur ${reponse.status}`;

  let charge: unknown;
  try {
    charge = await reponse.json();
  } catch {
    return { message: repli, erreurs: {} };
  }

  if (typeof charge !== "object" || charge === null) {
    return { message: repli, erreurs: {} };
  }

  const corps = charge as { erreur?: unknown; erreurs?: unknown };

  const erreurs: ErreursChamps = {};
  if (typeof corps.erreurs === "object" && corps.erreurs !== null) {
    for (const [champ, message] of Object.entries(corps.erreurs)) {
      if (typeof message === "string") erreurs[champ] = message;
    }
  }

  const message = typeof corps.erreur === "string" ? corps.erreur : (erreurs._global ?? repli);

  return { message, erreurs };
}

/** Appelle l'API et rend le corps JSON typé, ou lève une `ErreurApi`. */
export async function appelerApi<T>(chemin: string, options: OptionsRequete = {}): Promise<T> {
  const { methode = "GET", corps, toleranteAuxAnonymes = false, signal } = options;

  const entetes: Record<string, string> = { Accept: "application/json" };
  if (corps !== undefined) entetes["Content-Type"] = "application/json";

  const reponse = await fetch(chemin, {
    method: methode,
    headers: entetes,
    body: corps === undefined ? undefined : JSON.stringify(corps),
    // L'admin et l'API partagent l'origine derrière Caddy ; « include »
    // couvre aussi le cas où elles divergeraient.
    credentials: "include",
    signal,
  });

  if (!reponse.ok) {
    const { message, erreurs } = await lireErreur(reponse);

    if (reponse.status === 401 && !toleranteAuxAnonymes) {
      redirigerVersConnexion?.();
    }

    throw new ErreurApi(reponse.status, message, erreurs);
  }

  if (reponse.status === 204) return undefined as T;

  return (await reponse.json()) as T;
}

export const api = {
  get: <T>(chemin: string, options: Omit<OptionsRequete, "methode" | "corps"> = {}) =>
    appelerApi<T>(chemin, { ...options, methode: "GET" }),

  post: <T>(chemin: string, corps?: unknown, options: Omit<OptionsRequete, "methode"> = {}) =>
    appelerApi<T>(chemin, { ...options, methode: "POST", corps }),

  patch: <T>(chemin: string, corps?: unknown, options: Omit<OptionsRequete, "methode"> = {}) =>
    appelerApi<T>(chemin, { ...options, methode: "PATCH", corps }),

  supprimer: <T>(chemin: string, options: Omit<OptionsRequete, "methode" | "corps"> = {}) =>
    appelerApi<T>(chemin, { ...options, methode: "DELETE" }),
};
